/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import { useQuery } from '@apollo/client';
import { ShowMoreGridSection, ShowMoreGridSectionPlaceholder } from '@coursera-web/cds-labs';
import { intersectionBy, orderBy } from 'lodash';

import logger from 'js/app/loggerSingleton';

import {
  Button,
  Grid,
  PageGridContainer,
  Skeleton,
  Tag,
  Typography2,
  VisuallyHidden,
  breakpoints,
} from '@coursera/cds-core';
import { useTracker } from '@coursera/event-pulse/react';

import { DomainOrSmallSubsetSkillDataQueryText } from 'bundles/enterprise-learner-search/components/skillSearch/SkillSearchDataProvider';
import SkillMasteryLevelsForCurrentUser from 'bundles/levelsets/levelsets-v2/api/SkillMasteryLevelsForCurrentUser.graphql';
import type {
  SkillMasteryLevelFragment,
  SkillMasteryLevelsForCurrentUserQuery,
  SkillMasteryLevelsForCurrentUserQueryVariables,
} from 'bundles/levelsets/levelsets-v2/api/__generated__/SkillMasteryLevelsForCurrentUser';
import { LevelSetCard } from 'bundles/levelsets/levelsets-v2/components/LevelSetCard';
import type { Skill } from 'bundles/levelsets/levelsets-v2/types';
import { getSupportedSkills } from 'bundles/levelsets/levelsets-v2/utils/supportedSkills';
import { useProgramPinnedSkills } from 'bundles/levelsets/levelsets-v2/utils/useProgramPinnedSkills';
import HomeSection from 'bundles/page-config-common/components/shared/HomeSection';

import _t from 'i18n!nls/levelsets';

type MasteryLevel = SkillMasteryLevelFragment['masteryLevel'];

type SkillMasteryLevel = {
  skillId: string;
  masteryLevel: MasteryLevel;
  masteryPercent: number;
};

type LevelSetCardItem = {
  id: string;
  skill: Skill;
  masteryLevel?: MasteryLevel;
  masteryPercent?: number;
};

type Props = {
  programId: string;
};

const SHOW_MORE_AMOUNT = 6;

const styles = {
  container: css`
    display: flex;
    gap: var(--cds-spacing-600);
    background-color: var(--cds-color-emphasis-primary-background-xstrong);
    border-radius: 20px;
    padding: var(--cds-spacing-600);

    ${breakpoints.only('md')} {
      padding: var(--cds-spacing-400);
    }

    ${breakpoints.down('md')} {
      flex-direction: column;
      gap: var(--cds-spacing-300);
    }

    ${breakpoints.down('sm')} {
      padding: var(--cds-spacing-300) var(--cds-spacing-200);
    }
  `,
  descriptionArea: css`
    flex: 0 1 324px;

    ${breakpoints.down('md')} {
      flex: 1;
    }
  `,
  cardsArea: css`
    flex: 1;
  `,
  betaTag: css`
    color: var(--cds-color-neutral-primary-invert);
    margin-bottom: var(--cds-spacing-200);
  `,
  title: css`
    color: var(--cds-color-neutral-primary-invert);
    margin-bottom: var(--cds-spacing-100);
    font-size: 32px;

    ${breakpoints.down('md')} {
      font-size: 20px;
    }
  `,
  description: css`
    color: var(--cds-color-neutral-primary-invert);

    ${breakpoints.down('md')} {
      font-size: 14px;
    }
  `,
  loadingPlaceholderCard: css`
    border-radius: 16px;
  `,
  showMoreGridSection: css`
    padding: 0;

    ul:first-of-type {
      /* Switch from the component's default flex layout to a grid layout above mobile, otherwise there is no simple way
      to make a single final card in the last row line up with the left column instead of stretching across.
      https://stackoverflow.com/a/34930349 (the ::after trick did not work here) */
      display: grid;
      grid-template-columns: 50% 50%;
      grid-gap: var(--cds-spacing-200);
      justify-content: stretch;

      ${breakpoints.down('sm')} {
        display: flex;
        flex-direction: column;
        justify-content: space-between;
        gap: var(--cds-spacing-200);
      }

      & > li {
        /* The ShowMoreGrid component has logic that sometimes incorrectly sets max-width to 50%: */
        max-width: 100%;

        ${breakpoints.down('sm')} {
          flex: 1 1 100%;
        }

        & > div {
          /* If a shorter card without mastery is next to a taller card, ensure the shorter card grows to match the row height */
          min-height: 100%;
        }
      }
    }
  `,
};

/** Provides the data we need to pass to each <LevelSetCard />, in the desired order. */
const getCardItems = (
  programSkills: Skill[],
  currentUserSkillMasteryLevels: SkillMasteryLevel[],
  pinnedSkills: string[]
): LevelSetCardItem[] => {
  // Filter the program skills down to only the ones we support for LevelSets 2.0:
  const supportedSkills = getSupportedSkills();
  const supportedProgramSkills = intersectionBy(programSkills, supportedSkills, 'skillId');

  // Join each skill with the learner's current mastery level in it (if any) to create the card items:
  const levelSetCardItems = supportedProgramSkills.map((skill) => {
    const { masteryLevel, masteryPercent } =
      currentUserSkillMasteryLevels.find((skillMasteryLevel) => skillMasteryLevel.skillId === skill.skillId) || {};
    return { id: skill.skillId, skill, masteryLevel, masteryPercent };
  });

  const orderByColumns: ((item: LevelSetCardItem) => string | number)[] = [];
  const orderByDirections: ('asc' | 'desc')[] = [];

  // Show skills where you achieved mastery first, starting with your highest mastery:
  orderByColumns.push((item: LevelSetCardItem) => item.masteryLevel || 'MASTERY_LEVEL_0');
  orderByDirections.push('desc');

  // Next place pinned skills in order:
  orderByColumns.push((item: LevelSetCardItem) =>
    pinnedSkills.includes(item.skill.skillId) ? pinnedSkills.indexOf(item.skill.skillId) : Number.MAX_SAFE_INTEGER
  );
  orderByDirections.push('asc');

  // Finally, sort by skill name:
  orderByColumns.push((item: LevelSetCardItem) => item.skill.skillName);
  orderByDirections.push('asc');

  return orderBy(levelSetCardItems, orderByColumns, orderByDirections);
};

type LevelSetsCardsInnerProps = {
  loading: boolean;
  skillItems: LevelSetCardItem[];
};

const LevelSetsCardsInner = ({ loading, skillItems }: LevelSetsCardsInnerProps) => {
  const track = useTracker();

  return (
    <div css={styles.container}>
      <div css={styles.descriptionArea}>
        <Tag color="default" priority="primary" variant="status" css={styles.betaTag}>
          {_t('BETA')}
        </Tag>
        <Typography2 component="h1" variant="titleSmall" css={styles.title}>
          {_t('Evaluate your strengths')}
        </Typography2>
        <Typography2 component="p" variant="bodyPrimary" css={styles.description}>
          {_t('Complete a skill diagnostic so we can connect you to the right content for your level.')}
        </Typography2>
      </div>

      <div css={styles.cardsArea}>
        <VisuallyHidden role="status">{loading ? _t('Loading cards') : _t('Loaded cards')}</VisuallyHidden>
        {loading ? (
          <ShowMoreGridSectionPlaceholder
            css={styles.showMoreGridSection}
            hideTitle
            itemsGridConfig={{ xs: 12, sm: 12, md: 6, lg: 6 }}
            defaultRows={1}
            renderPlaceholderItem={() => <Skeleton css={styles.loadingPlaceholderCard} variant="rect" height={148} />}
          />
        ) : (
          <ShowMoreGridSection<LevelSetCardItem>
            css={styles.showMoreGridSection}
            items={skillItems}
            itemsGridConfig={{ xs: 12, sm: 12, md: 6, lg: 6 }}
            defaultRows={1}
            showMoreAmount={SHOW_MORE_AMOUNT}
            renderItem={({ item, itemIndex }) => (
              <LevelSetCard
                skill={item.skill}
                masteryLevel={item.masteryLevel}
                masteryPercent={item.masteryPercent}
                index={itemIndex}
              />
            )}
            onExpandButtonClick={({ numberOfItemsDisplayed }) => {
              // This fires just before the expansion or collapse actually happens
              const isCollapseButton = numberOfItemsDisplayed === skillItems.length;
              const numItemsDisplayedExcludingInitial = Math.max(0, numberOfItemsDisplayed - 2);
              const totalNumItemsExcludingInitial = Math.max(0, skillItems.length - 2);
              const pageNumBeforeClick = Math.ceil(numItemsDisplayedExcludingInitial / SHOW_MORE_AMOUNT);
              const nextPageNum = isCollapseButton ? 0 : pageNumBeforeClick + 1;
              const totalPages = Math.ceil(totalNumItemsExcludingInitial / SHOW_MORE_AMOUNT) + 1;
              track(isCollapseButton ? 'collapse_carousel' : 'expand_carousel', {
                carousel: {
                  id: 'levelsets_cards',
                  pagination: {
                    pageNumber: nextPageNum,
                    pageSize: SHOW_MORE_AMOUNT,
                    totalItems: skillItems.length,
                    totalPages,
                  },
                },
              });
            }}
            renderExpandButton={({ onClick, defaultLabel, isExpanded, showMoreCount, ...rest }) => {
              const isCollapseButton = isExpanded && showMoreCount === 0;
              return (
                <Button
                  aria-label={defaultLabel}
                  size="small"
                  variant="secondary"
                  onClick={onClick}
                  fullWidth={false}
                  {...rest}
                >
                  {isCollapseButton ? _t('Show less') : _t('Show more')}
                </Button>
              );
            }}
          />
        )}
      </div>
    </div>
  );
};

export const LevelSetsCards = ({ programId }: Props) => {
  const {
    data,
    loading: programSkillsLoading,
    error,
  } = useQuery(DomainOrSmallSubsetSkillDataQueryText, {
    context: { clientName: 'gatewayGql' },
    variables: { programId },
  });
  const { data: currentUserMasteryLevelsData, loading: currentUserMasteryLevelsLoading } = useQuery<
    SkillMasteryLevelsForCurrentUserQuery,
    SkillMasteryLevelsForCurrentUserQueryVariables
  >(SkillMasteryLevelsForCurrentUser, {
    context: { clientName: 'gatewayGql' },
  });
  const { loading: pinnedSkillsLoading, pinnedSkills } = useProgramPinnedSkills(programId);

  if (error) {
    // Ignoring errors from  here SkillMasteryLevelsForCurrentUserQuery, because there will be BE logs for these issues
    // and if we can't load the user's mastery levels we can still show the LevelSet cards without this
    logger.error('Error loading skill data for LevelSetsCards:', error);
    return null;
  }

  const loading = programSkillsLoading || currentUserMasteryLevelsLoading || pinnedSkillsLoading;
  const programSkills = ((data as $TSFixMe)?.DomainOrSmallSubsetSkillDataQuery?.skills || []) as Skill[];
  const currentUserSkillMasteryLevels =
    currentUserMasteryLevelsData?.SkillMasteryLevels?.querySkillsMasteryLevels?.elements?.map(
      ({ skill, masteryLevel, masteryPercent }) => ({ skillId: skill.id, masteryLevel, masteryPercent })
    ) || [];
  const skillItems = getCardItems(programSkills, currentUserSkillMasteryLevels, pinnedSkills);

  if (!skillItems?.length) {
    return null;
  }

  return (
    <HomeSection>
      <PageGridContainer>
        <Grid item xs={12}>
          <LevelSetsCardsInner loading={loading} skillItems={skillItems} />
        </Grid>
      </PageGridContainer>
    </HomeSection>
  );
};

export default LevelSetsCards;
